import { useEffect, useState } from "react";
import { Megaphone, X, MapPin, Calendar } from "lucide-react";

/**
 * One-day event announcement bar with a live countdown to the launch moment.
 * Visible until the end of 25 Aug 2026 IST; hidden after and dismissible.
 */
const EVENT_START_UTC = new Date("2026-08-25T05:00:00Z"); // 25 Aug 2026 · 10:30 IST
const EVENT_END_UTC = new Date("2026-08-25T18:30:00Z");   // 25 Aug 2026 · 24:00 IST
const STORAGE_KEY = "topnews-dismiss-parinitha-2026-08-25";

function pad(n) { return String(n).padStart(2, "0"); }

function diff(target) {
  const ms = Math.max(0, target.getTime() - Date.now());
  const s = Math.floor(ms / 1000);
  return {
    ms,
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

export default function TopNewsBar() {
  const [show, setShow] = useState(false);
  const [t, setT] = useState(() => diff(EVENT_START_UTC));

  useEffect(() => {
    if (Date.now() > EVENT_END_UTC.getTime()) return;
    if (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY)) return;
    setShow(true);
    const id = setInterval(() => setT(diff(EVENT_START_UTC)), 1000);
    return () => clearInterval(id);
  }, []);

  if (!show) return null;

  const dismiss = () => {
    window.localStorage.setItem(STORAGE_KEY, "1");
    setShow(false);
  };

  const live = t.ms === 0 && Date.now() < EVENT_END_UTC.getTime();

  const Seg = ({ v, l, testid }) => (
    <div className="flex flex-col items-center leading-none">
      <span data-testid={testid} className="stat-num text-sm sm:text-base tabular-nums text-white">{pad(v)}</span>
      <span className="text-[8px] uppercase tracking-widest text-white/70 font-bold mt-0.5">{l}</span>
    </div>
  );

  return (
    <div data-testid="top-news-bar" className="relative bg-coral text-white overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-15" style={{ backgroundImage: "linear-gradient(45deg, rgba(255,255,255,.35) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.35) 50%, rgba(255,255,255,.35) 75%, transparent 75%)", backgroundSize: "18px 18px" }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="hidden sm:inline-flex w-7 h-7 rounded-full bg-white/20 items-center justify-center shrink-0 animate-pulse-soft">
            <Megaphone className="w-3.5 h-3.5" />
          </span>
          <div className="min-w-0 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1 bg-white text-coral font-display font-extrabold text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm">
              {live ? "Live Today" : "Top News"}
            </span>
            <span className="font-display font-bold text-xs sm:text-sm truncate">
              <span className="font-extrabold">Free Uniform &amp; Book Distribution</span>
              <span className="hidden sm:inline"> — Parinitha School, Hallagere (Mandya)</span>
            </span>
            <span className="hidden md:inline-flex items-center gap-1 text-white/85 text-xs font-display">
              <Calendar className="w-3.5 h-3.5" /> 25 Aug 2026 · 10:30 AM
            </span>
            <span className="hidden lg:inline-flex items-center gap-1 text-white/85 text-xs font-display">
              <MapPin className="w-3.5 h-3.5" /> Hallagere, Mandya
            </span>
          </div>
        </div>

        {/* Countdown */}
        {live ? (
          <div data-testid="event-countdown-live" className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-sm bg-white/15 border border-white/25">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span className="font-display font-extrabold text-[11px] uppercase tracking-widest">Happening Now</span>
          </div>
        ) : (
          <div data-testid="event-countdown" className="hidden sm:flex items-center gap-2 shrink-0">
            <span className="hidden md:inline text-[10px] uppercase tracking-widest font-bold text-white/70">Starts in</span>
            <div className="flex items-center gap-1.5 bg-white/12 border border-white/20 rounded-sm px-2.5 py-1">
              <Seg v={t.days} l="days" testid="cd-d" />
              <span className="text-white/50 stat-num text-sm">:</span>
              <Seg v={t.hours} l="hrs" testid="cd-h" />
              <span className="text-white/50 stat-num text-sm">:</span>
              <Seg v={t.minutes} l="min" testid="cd-m" />
              <span className="text-white/50 stat-num text-sm">:</span>
              <Seg v={t.seconds} l="sec" testid="cd-s" />
            </div>
          </div>
        )}

        <button
          data-testid="top-news-dismiss"
          onClick={dismiss}
          aria-label="Dismiss announcement"
          className="shrink-0 w-7 h-7 flex items-center justify-center hover:bg-white/15 rounded-sm transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
